import { useMutation, useQueryClient } from "@tanstack/react-query";

import type {
  ActionItemFeedItem,
  FeedItem,
  KeyQuestionAnswerFeedItem,
} from "@app/analytics/Home/types";
import apiClient from "@app/common/api/apiClient";
import {
  ActionItemChipStatus,
  KeyQuestionAnswerClipStatus,
} from "@app/common/types";
import { removeAt, replaceAt } from "@app/utils/arrays";

const useUpdateClips = () => {
  const queryClient = useQueryClient();
  const homePageQueryKey = ["home", "feed"];

  return useMutation({
    mutationFn: apiClient.clips.updateClip,
    onMutate: async (body) => {
      await queryClient.cancelQueries({ queryKey: homePageQueryKey });
      const data = queryClient.getQueryData(homePageQueryKey) as FeedItem[];

      let updatedFeedData;

      const indexOfItemInFeed = data.findIndex(
        (item) => item.entityId === body.id && item.type === body.type
      );
      const feedItem = data[indexOfItemInFeed];

      if (body.type === "action_item" && feedItem.type === "action_item") {
        if (body.status === "user_reject") {
          updatedFeedData = removeAt(data, indexOfItemInFeed);
        } else {
          const newActionItem: ActionItemFeedItem = {
            ...feedItem,
            data: {
              ...feedItem.data,
              status: body.status as ActionItemChipStatus,
            },
          };

          updatedFeedData = replaceAt(data, newActionItem, indexOfItemInFeed);
        }
      }

      if (
        body.type === "key_question_answer" &&
        feedItem.type === "key_question_answer"
      ) {
        const newAnswer: KeyQuestionAnswerFeedItem = {
          ...feedItem,
          data: {
            ...feedItem.data,
            status: body.status as KeyQuestionAnswerClipStatus,
          },
        };

        updatedFeedData = replaceAt(data, newAnswer, indexOfItemInFeed);
      }

      if (updatedFeedData) {
        queryClient.setQueryData(homePageQueryKey, updatedFeedData);
      }
    },
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: homePageQueryKey }),
  });
};

export default useUpdateClips;
